import React from 'react';
import {FormGroup} from 'arag-ui-lib';
import { FORM_GROUP_VARIANTS } from './formGroupVariants';
import { formattedMoney, roundToTwoDecimals, formatSolicitorTableLabel } from './functions';
import { CURRENCY, PERCENTAGE_SYMBOL } from './variables';
import CONTENT_STRINGS from './contentStrings';
import {
	PREMIUM,
	RECOVERABLE,
	NON_RECOVERABLE,
	UNDERWRITER_RATE,
	UNDERWRITER_RATE_PERCENTAGE,
	COMMISSION,
	COMMISSION_PERCENTAGE,
	INITIAL_OPTIONS,
	QUESTION,
	EDITABLE_BY,
	REVERSE,
	ESTIMATED_MEDICAL_EXPERT_FEES_MN,
} from './constants';
import EditIcon from '../components/icons/editIcon';
import DeleteIcon from '../components/icons/deleteIcon';
import InfoIcon from '../components/icons/infoIcon';
import { calculateChildQuestionOptionName } from './applicationQuestionsUtils';
import { getFieldStyles } from './applicationPremiumSettingsFunctions';

export const medicalFeesTableColumns = (isEditable, breachCausationCpOptions, updateHandler, key) => [
	{
		propertyName: 'name',
		header: CONTENT_STRINGS.NAME,
		id: 'name',
		sortable: false,
		type: 'text',
		formatter: (row) => {
			return isEditable ? (
				<FormGroup
					inputSize={"col-sm-12"}
					label={''}
					variant={FORM_GROUP_VARIANTS.TEXT}
					inputProps={{
						id: `name-${row.id}`,
						name: 'name',
						onChange: (e) => updateHandler({
							key: row[key],
							name: e.target.name,
							value: e.target.value
						}),
						value: row.name,
						autoComplete: 'off',
					}}
				/>
			) : (
				<span>{row.name}</span>
			);
		}
	},
	{
		propertyName: 'natureOfExpert',
		header: CONTENT_STRINGS.NATURE_OF_EXPERT,
		id: 'natureOfExpert',
		sortable: false,
		type: 'text',
		formatter: (row) => {
			return isEditable ? (
				<FormGroup
					inputSize={"col-sm-12"}
					label={''}
					variant={FORM_GROUP_VARIANTS.TEXT}
					inputProps={{
						id: `natureOfExpert-${row.id}`,
						name: 'natureOfExpert',
						onChange: (e) => updateHandler({
							key: row[key],
							name: e.target.name,
							value: e.target.value
						}),
						value: row.natureOfExpert,
						autoComplete: 'off',
					}}
				/>
			) : (
				<span>{row.natureOfExpert}</span>
			);
		}
	},
	{
		propertyName: 'whySpecificExpert',
		header: CONTENT_STRINGS.WHY_SPECIFIC_EXPERT,
		id: 'whySpecificExpert',
		sortable: false,
		type: 'text',
		formatter: (row) => {
			return isEditable ? (
				<FormGroup
					inputSize={"col-sm-12"}
					label={''}
					variant={FORM_GROUP_VARIANTS.TEXT}
					inputProps={{
						id: `whySpecificExpert-${row.id}`,
						name: 'whySpecificExpert',
						onChange: (e) => updateHandler({
							key: row[key],
							name: e.target.name,
							value: e.target.value
						}),
						value: row.whySpecificExpert,
						autoComplete: 'off',
					}}
				/>
			) : (
				<span>{row.whySpecificExpert}</span>
			);
		}
	},
	{
		propertyName: 'breachCausationCpId',
		header: CONTENT_STRINGS.BREACH_CAUSATION_CP,
		id: 'breachCausationCpId',
		sortable: false,
		type: 'text',
		formatter: (row) => {
			const options = [...INITIAL_OPTIONS, ...breachCausationCpOptions.options.map(option => {
				return {
					key: option.id,
					label: option.name
				}
			})];
			const plainValue = options.find(option => option.key === row.breachCausationCpId);
			return isEditable ? (
				<FormGroup
					inputSize={"col-sm-12"}
					label={''}
					variant={FORM_GROUP_VARIANTS.SELECT}
					inputProps={{
						id: `breachCausationCpId-${row.id}`,
						name: 'breachCausationCpId',
						onChange: (e) => updateHandler({
							key: row[key],
							name: e.target.name,
							value: e.target.value
						}),
						value: row.breachCausationCpId,
						options: options,
					}}
				/>
			) : (
				<span>{plainValue.label}</span>
			);
		}
	},
	{
		propertyName: 'partialEstimatedCost',
		header: CONTENT_STRINGS.ESTIMATED_COST_OF_REPORT,
		id: 'partialEstimatedCost',
		sortable: false,
		type: 'number',
		formatter: (row) => {

			return isEditable ? (
				<FormGroup
					inputSize={"col-sm-12"}
					label={''}
					variant={FORM_GROUP_VARIANTS.TEXT}
					inputProps={{
						id: `partialEstimatedCost-${row.id}`,
						name: 'partialEstimatedCost',
						onChange: (e) => updateHandler({
							key: row[key],
							name: e.target.name,
							value: e.target.value
						}),
						value: row.partialEstimatedCost,
						type: 'number',
						autoComplete: 'off',
					}}
				/>
			) : (
				<span>{row.partialEstimatedCost}</span>
			);
		}
	},
	{
		propertyName: 'attendanceEstimatedCost',
		header: CONTENT_STRINGS.ESTIMATED_COST_OF_ATTENDANCE,
		id: 'attendanceEstimatedCost',
		sortable: false,
		type: 'number',
		formatter: (row) => {
			return isEditable ? (
				<FormGroup
					inputSize={"col-sm-12"}
					label={''}
					variant={FORM_GROUP_VARIANTS.TEXT}
					inputProps={{
						id: `attendanceEstimatedCost-${row.id}`,
						name: 'attendanceEstimatedCost',
						onChange: (e) => updateHandler({
							key: row[key],
							name: e.target.name,
							value: e.target.value
						}),
						value: row.attendanceEstimatedCost,
						type: 'number',
						autoComplete: 'off',
					}}
				/>
			) : (
				<span>{row.attendanceEstimatedCost}</span>
			);
		}
	},
];

export const medicalExpertFeesActionColumn = (
	variant,
	isEditable,
	handleEditRowClick,
	openDeleteAnswerModal,
	openHistoryModal,
	question,
	approvalSettings,
	informationText,
	isModified,
	medicalExpertFees = [],
) => [
	{
		propertyName: 'actions',
		header: CONTENT_STRINGS.ACTIONS,
		id: 'actions',
		formatter: (row) => (
			<div style={{ display: 'flex', justifyContent: 'flex-start', gap: '5px' }}>
				{row.id && isEditable
				?
					<EditIcon
						className={ approvalSettings.changesRequiresARAGApproval }
						onClick={() => handleEditRowClick(row.id)}
						variant={variant}
						editIconTestId={`policy-application-edit-icon-${row.id}`}
						informationText={ informationText }
					/>
				: 
					null
				}
				{medicalExpertFees.length > 1 && isEditable
				?
					<DeleteIcon
						className={ approvalSettings.changesRequiresARAGApproval }
						onClick={() => { openDeleteAnswerModal(row, approvalSettings.editingSetting, ESTIMATED_MEDICAL_EXPERT_FEES_MN) }}
						variant={variant}
						deleteIconTestId={`policy-application-delete-icon-${row.id}`}
					/>
				: 
					null
				}
				{isModified(row.id)
				?
					<InfoIcon
						className={ approvalSettings.changesRequiresARAGApproval }
						onClick={() => openHistoryModal(question, row.id)}
						infoIconTestId={`policy-application-info-icon-${row.id}`}
						variant={variant}
					/>
				: 
					null
				}
			</div>
		)
	}
]

//Table for Premium Settings both Premium Settings Wizard, Premium Settings and Application Premium Settings. Both for Readonly and Editable tables.
export const premiumSettingsTableColumns = (
	headerData,
	isPercentagePremium,
	showtable = false,
	isApplicationPremium = false,
	isEditable = false,
	handleEditableData = () => {},
	emptyField = '',
	tableErrors = {},
	// WI 97790: freeze the contingent Agent Commission and Underwriter Premium cells. Contingent rows only -
	// the non-contingent section stays fully editable.
	lockContingentCommission = false,
	isUnderwriterPremiumLocked = false
) => {
	const firstColumn = {
		propertyName: 'chargeType',
		header: '',
		id: 'chargeType',
		sortable: false,
		type: 'text',
		// Commission-agent rows carry solicitorId + aragProvider (see handlePremiumSettingsTableData) -
		// render those with the agent's coloured line, same as every other Table using formatSolicitorTableLabel.
		// Only during an active wizard run (showtable false) though - on a live policy/application the
		// same ARAG provider is already shown once via applicationPolicyViewHeader.js, and the combination
		// tables (premiumSettingsCombinationTable.js/stagedPremiumSettingsCombinationTable.js) will get
		// their own solicitor-group-based colouring in a later story, so a per-agent marker here would be
		// redundant/premature in both places.
		formatter: (row) => row.solicitorId != null
			? formatSolicitorTableLabel({
				label: (
					<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
						<p>{row.chargeType}</p>
						{row.commissionId && <p>{row.commissionId}</p>}
						<p>{CONTENT_STRINGS.COMMISSION}</p>
					</div>
				),
				aragProvider: showtable ? null : row.aragProvider
			})
			: row.chargeType,
	};
	let premiumChargesColumn = [];
	// Loop through headerData starting from index 0 to create premiumChargesColumn
	headerData.forEach((item, index) => {
		const columnIndex = index;
		premiumChargesColumn.push({
			propertyName: `premiumCharge${index}`,
			header: item.label,
			id: `premiumCharge${index}`,
			sortable: false,
			type: 'number',
			formatter: (value) => {
				const premiumCharges = value.data;//value.data contains the data for each row. Premium row, Underwriter row, etc. Assigned to premiumCharges
				const amount = premiumCharges[columnIndex].amount;//Access each object from premiumCharges and split it to amount
				const percentage = premiumCharges[columnIndex].percentage;//And percentage
				const amountName = Array.isArray(value.name) ? value.name[0] : value.name;//Distinguish between different amount names
				const percentageName = Array.isArray(value.name) ? value.name[1] : '';//And percentage names
				// Always display money at 2dp, regardless of the real underlying precision
				// (auto-calculated amounts like underwriterRate/commission are stored with up
				// to 4dp for accuracy — only the display is rounded).
				const formattedAmount = amount != null ? formattedMoney(amount, CURRENCY) : 0;
				// `amount`/`percentage` above are rebuilt from state on every render via
				// Number(...), which silently drops a trailing "." or trailing zeros (e.g.
				// "10." -> 10) — fine for formatting/comparisons, but if used as the editable
				// input's live value it would erase a decimal point the instant it's typed,
				// before the next digit can land. `rawAmount`/`rawPercentage` are the untouched
				// strings from state, used only for what the editable inputs display.
				const rawAmount = premiumCharges[columnIndex].rawAmount;
				const rawPercentage = premiumCharges[columnIndex].rawPercentage;
				// For the live editable input: if this amount was auto-calculated from its
				// sibling percentage (not typed directly by the user right now), show it
				// rounded to 2dp too — without touching the real value kept in state/`amount`,
				// which is what still gets read for onChange and for submission. If the user
				// is actively typing this exact field, show their raw (already ≤2dp) input
				// untouched so we don't fight their keystrokes.
				const amountIsDerived = premiumCharges[columnIndex].amountIsDerived;
				const editableAmountValue = (amountIsDerived && amount !== '' && amount != null)
					? roundToTwoDecimals(amount)
					: (rawAmount ?? amount);
				const editablePercentageValue = rawPercentage ?? percentage;
				const formattedPercentage = (percentage != null && percentage !== '') ? `${percentage} ${PERCENTAGE_SYMBOL}` : `0 ${PERCENTAGE_SYMBOL}`;
				// GPL AC1/AC2/AC3/AC4 - GPL (Green/LEI) policies do not use Underwriter Premiums: when
				// locked, both fields always display zero and are disabled, regardless of the live
				// tableData value (the backend clamp is authoritative on save; this defends the display
				// against pre-clamp historical data such as an older revision taken before this feature
				// existed).
				const isLockedUnderwriterField = isUnderwriterPremiumLocked
					&& amountName === UNDERWRITER_RATE && percentageName === UNDERWRITER_RATE_PERCENTAGE;
				const displayEditableAmountValue = isLockedUnderwriterField ? 0 : editableAmountValue;
				const displayEditablePercentageValue = isLockedUnderwriterField ? 0 : editablePercentageValue;
				const displayFormattedAmount = isLockedUnderwriterField ? formattedMoney(0, CURRENCY) : formattedAmount;
				const displayFormattedPercentage = isLockedUnderwriterField ? `0 ${PERCENTAGE_SYMBOL}` : formattedPercentage;
				const amountErrorMessage = tableErrors?.[`${amountName}-${columnIndex}-${value.stageId ?? ''}-${value.solicitorId ?? ''}-${!!value.isNonContingent}`] || (amount < 0 ? CONTENT_STRINGS.NEGATIVE_FIELDS : amount === '' ? emptyField : '');
				const percentageErrorMessage = tableErrors?.[`${percentageName}-${columnIndex}-${value.stageId ?? ''}-${value.solicitorId ?? ''}-${!!value.isNonContingent}`] || (percentage < 0 ? CONTENT_STRINGS.NEGATIVE_FIELDS : percentage === '' ? emptyField : '');
				const additionalStyle = getFieldStyles(`premiumCharge${columnIndex}`, value?.emphasizedFields, value?.highlightedFields);
				if (amountName === PREMIUM || amountName === RECOVERABLE || amountName === NON_RECOVERABLE) {
					if (isEditable) {
						return (
							<div className={"single-values-cell"}>
								<FormGroup
									labelSize={'col-sm-2'}
									inputSize={'col-sm-8'}
									label={CURRENCY}
									variant={FORM_GROUP_VARIANTS.TEXT}
									inputProps={{
										value: editableAmountValue,
										name: amountName,
										// Not type="number" — a native number input silently reports
										// e.target.value as '' whenever the typed text becomes an
										// invalid float (e.g. a second '.' mid-string), hiding the bad
										// input from hasTooManyDecimals entirely. Sanitize instead,
										// same as step5.js's Limit of Indemnity field.
										onChange: (e) => {
											handleEditableData(e.target.value.replace(/[^0-9.]/g, ''), e.target.name, value, columnIndex);
										},
										autoComplete: 'off',
									}}
									formGroupTestId={`application-premium-settings-wizard-${amountName}-${columnIndex}-amount`}
									errorMessage={amountErrorMessage}
								/>
							</div>
						);
					} else {
						return (
							<div className={"single-values-cell"}>
								<FormGroup
									labelSize={'col-sm-12'}
									inputSize={''}
									label={formattedAmount}
									variant={FORM_GROUP_VARIANTS.PLAIN}
									inputProps={{
										name: amountName,
										additionalStyle: additionalStyle,
									}}
								/>
							</div>
						);
					}
				} else if (
					(amountName === UNDERWRITER_RATE && percentageName === UNDERWRITER_RATE_PERCENTAGE) ||
					(amountName === COMMISSION && percentageName === COMMISSION_PERCENTAGE)
				) {
					// WI 97790: on a restricted policy the contingent Agent Commission and Underwriter
					// Premium cells fall through to the plain-text arm below - the same rendering the
					// show view already uses. Non-contingent cells are untouched.
					const cellIsFrozen = lockContingentCommission && !value.isNonContingent;
					if (isEditable && !cellIsFrozen) {
						return (
							<div style={{ minWidth: "max-content" }}>
								<div className={"multiple-values-cell"}>
									{
										!isPercentagePremium
										?
											<FormGroup
												labelSize={'col-sm-2'}
												inputSize={'col-sm-8'}
												label={CURRENCY}
												variant={FORM_GROUP_VARIANTS.TEXT}
												inputProps={{
													value: displayEditableAmountValue,
													name: amountName,
													disabled: isLockedUnderwriterField,
													// See the amount field above — not type="number", for the
													// same reason (avoid the invalid-float value-swallowing quirk).
													onChange: (e) => {
														handleEditableData(e.target.value.replace(/[^0-9.]/g, ''), e.target.name, value, columnIndex);
													},
													autoComplete: 'off',
												}}
												formGroupTestId={`application-premium-settings-wizard-${amountName}-${columnIndex}-amount`}
												errorMessage={amountErrorMessage}
											/>
										:
											null
									}
									<FormGroup
										labelSize={'col-sm-2'}
										inputSize={'col-sm-8'}
										label={''}
										variant={FORM_GROUP_VARIANTS.TEXT}
										inputProps={{
											value: displayEditablePercentageValue,
											name: percentageName,
											disabled: isLockedUnderwriterField,
											onChange: (e) => {
												handleEditableData(e.target.value.replace(/[^0-9.]/g, ''), e.target.name, value, columnIndex);
											},
											autoComplete: 'off',
										}}
										hasInlineElements={true}
										inlineGroupElements={
											<div style={{marginTop: '7px'}}> {PERCENTAGE_SYMBOL} </div>
										}
										formGroupTestId={`application-premium-settings-wizard-${percentageName}-${columnIndex}-percentage`}
										errorMessage={percentageErrorMessage}
									/>
								</div>
							</div>
						);
					} else {
						// We show the amount when:
						const showFormattedAmount =
                            (showtable && isApplicationPremium) || // We are on an Application that has Premium Settings.
                            (showtable && !isApplicationPremium && !isPercentagePremium) || // We are on Premium Settings Page and the Premium Settings are not in Percentage mode.
                            (!showtable && !isPercentagePremium);// We are on the Step6 of the wizard and the Premium Settings are not in Percentage mode.
						return (
							<div style={{ minWidth: "max-content" }}>
								<div className={showFormattedAmount ? "multiple-values-cell" : "single-values-cell"}>
									{showFormattedAmount
										?
											<FormGroup
												labelSize={'col-sm-12'}
												inputSize={''}
												label={displayFormattedAmount}
												variant={FORM_GROUP_VARIANTS.PLAIN}
												inputProps={{
													name: amountName,
													additionalStyle: additionalStyle,
												}}
											/>
										:
											null
									}
									<FormGroup
										labelSize={'col-sm-12'}
										inputSize={''}
										label={displayFormattedPercentage}
										variant={FORM_GROUP_VARIANTS.PLAIN}
										inputProps={{
											name: percentageName,
											additionalStyle: additionalStyle,
										}}
									/>
								</div>
							</div>
						);
					}
				}
			}
		});
	});
  	const columns = [firstColumn, ...premiumChargesColumn];
  	return columns;
};

// We maintain two different function signatures for backward compatibility
export const readonlyPremiumSettingsTableColumns = (headerData, isPercentagePremium, showtable, isApplicationPremium, emphasizedFields = null, highlightedFields = null, isUnderwriterPremiumLocked = false) => {
    const isEditable = false;
    const handleEditableData = () => {};
    const emptyField = '';
    // premiumSettingsTableColumns's 8th/9th positional params are tableErrors/lockContingentCommission,
    // not this wrapper's own emphasizedFields/highlightedFields - passing those straight through bound
    // them to the wrong slots (WI 97790 added a 9th param, which is what turned a harmless positional
    // drop into a live wrong-slot binding). Neither applies to a readonly table: there is no error map to
    // show, and isEditable is always false here so lockContingentCommission's cellIsFrozen check
    // (gated on `isEditable && !cellIsFrozen`) could never fire regardless of its value.
    // Both `emphasizedFields` and `highlightedFields` end up simply unused here as a result - neither is
    // forwarded to premiumSettingsTableColumns under any name, and this wrapper has nowhere else to apply
    // them - so they're kept out of this call (rather than appending isUnderwriterPremiumLocked as a 10th,
    // still-dropped argument after them) so the new GPL lock flag actually reaches
    // premiumSettingsTableColumns's isUnderwriterPremiumLocked parameter instead of silently becoming a
    // no-op for every readonly view (Overview step, combination table, revision modal).
    const tableErrors = {};
    const lockContingentCommission = false;
  	return premiumSettingsTableColumns(headerData, isPercentagePremium, showtable, isApplicationPremium, isEditable, handleEditableData, emptyField, tableErrors, lockContingentCommission, isUnderwriterPremiumLocked);
};

export const editablePremiumSettingsColumns = (headerData, handleEditableData, isPercentagePremium, emptyField, tableErrors, lockContingentCommission = false, isUnderwriterPremiumLocked = false) => {
	const showtable = false;
	const isApplicationPremium = false;
	const isEditable = true;
  	return premiumSettingsTableColumns(headerData, isPercentagePremium, showtable, isApplicationPremium, isEditable, handleEditableData, emptyField, tableErrors, lockContingentCommission, isUnderwriterPremiumLocked);
};

// Table for Editing Settings Page
export const handleEditingSettingsColumns = (
	questions, 
	changeEditingSettings, 
	editingSettingsOptions, 
) => {
	let columns = [];

	 	columns.push({ 
			propertyName: QUESTION,
			header: CONTENT_STRINGS.QUESTION, 
			id: QUESTION, 
			style: { width: '65%' },
			formatter: (value) => {
				let question = value[QUESTION];
				if(question.isParent === true) {
					return (
						<FormGroup
							label={question.name}
							labelSize="col-sm-12"
							variant={FORM_GROUP_VARIANTS.PLAIN}
							inputProps={{
								name: question.id,
								required: question.isRequired,
							}}
						/>
					)
				} else {
					return (
						<FormGroup
							label= {" - " + question.name}
							inputSize="col-sm-12"
							labelSize="col-sm-12"
							variant={FORM_GROUP_VARIANTS.PLAIN}
							groupShape={REVERSE}
							inputProps={{
								name: question.id,
								label: calculateChildQuestionOptionName(question.id, questions),
								required: question.isRequired,
							}}
						/>
					)
				}
			}
		});
		columns.push({ 
			propertyName: EDITABLE_BY, 
			header: CONTENT_STRINGS.EDITABLE_BY, 
			id: EDITABLE_BY, 
			style: { width: '35%' },
			formatter: (value) => {
				let editingSetting = value[EDITABLE_BY];
				if(editingSetting && editingSetting.id) {
					return (
						<FormGroup
							label=""
							inputSize="col-sm-12"
							variant={FORM_GROUP_VARIANTS.SELECT}
							inputProps={{
								name: editingSetting.id,
								value: editingSetting.value,
								onChange: (evt) => changeEditingSettings({ key: evt.target.name, value: evt.target.value }),
								options: editingSettingsOptions.map(option => ({
									key: option.key,
									label: option.value
								})),
							}}
						/>
					);
				}
			} 
			}
		)
	return columns;	
}